import type {
  AddForecastEventInput,
  AddForecastVariableInput,
  ForecastEvent,
  ForecastProjection,
  ForecastProjectionQuery,
  ForecastScenario,
  ForecastTargetEvaluation,
  ForecastTargetEvaluationQuery,
  ForecastVariable,
} from '@taler/api-client'
import { forecastKeys } from '@taler/api-client'
import { formatLocalDate, type LocalDate } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { problemDetail } from '../internal/problem.ts'

export interface UseForecastScenarioOptions {
  readonly ledgerId: () => string | undefined
  readonly scenarioId: () => string
  readonly enabled: () => boolean
  readonly from: () => LocalDate
  readonly through: () => LocalDate
  readonly displayCurrency: () => string
  /** Undefined when no target is chosen to evaluate this scenario against. */
  readonly targetId: () => string | undefined
}

export interface UseForecastScenario {
  readonly scenario: ComputedRef<ForecastScenario | undefined>
  readonly variables: ComputedRef<readonly ForecastVariable[]>
  readonly events: ComputedRef<readonly ForecastEvent[]>
  readonly projection: ComputedRef<ForecastProjection | undefined>
  readonly isProjecting: ComputedRef<boolean>
  readonly projectionError: ComputedRef<Error | null>
  readonly projectionRetrying: ComputedRef<boolean>
  readonly targetEvaluation: ComputedRef<ForecastTargetEvaluation | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly isCreatingVariable: ComputedRef<boolean>
  readonly hasVariableError: ComputedRef<boolean>
  readonly variableErrorDetail: ComputedRef<string | undefined>
  readonly deletingVariableId: ComputedRef<string | undefined>
  readonly isCreatingEvent: ComputedRef<boolean>
  readonly hasEventError: ComputedRef<boolean>
  readonly eventErrorDetail: ComputedRef<string | undefined>
  readonly deletingEventId: ComputedRef<string | undefined>
  createVariable(input: AddForecastVariableInput): Promise<ForecastVariable>
  deleteVariable(variableId: string): Promise<void>
  createEvent(input: AddForecastEventInput): Promise<ForecastEvent>
  deleteEvent(eventId: string): Promise<void>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useForecastScenario(options: UseForecastScenarioOptions): UseForecastScenario {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const scenarioQuery = useQuery({
    queryKey: computed(() => forecastKeys.scenario(options.ledgerId() ?? '', options.scenarioId())),
    queryFn: ({ signal }) => api.forecast.getScenario(options.ledgerId()!, options.scenarioId(), signal),
    enabled: active,
    retry: false,
  })

  const variablesQuery = useQuery({
    queryKey: computed(() => forecastKeys.variables(options.ledgerId() ?? '', options.scenarioId())),
    queryFn: ({ signal }) => api.forecast.listVariables(options.ledgerId()!, options.scenarioId(), signal),
    enabled: active,
    retry: false,
  })

  const eventsQuery = useQuery({
    queryKey: computed(() => forecastKeys.events(options.ledgerId() ?? '', options.scenarioId())),
    queryFn: ({ signal }) => api.forecast.listEvents(options.ledgerId()!, options.scenarioId(), signal),
    enabled: active,
    retry: false,
  })

  const projectionQuery = computed<ForecastProjectionQuery>(() => ({
    from: formatLocalDate(options.from()),
    through: formatLocalDate(options.through()),
    displayCurrency: options.displayCurrency() as ForecastProjectionQuery['displayCurrency'],
  }))

  const projectionResult = useQuery({
    queryKey: computed(() =>
      forecastKeys.projection(options.ledgerId() ?? '', options.scenarioId(), projectionQuery.value),
    ),
    queryFn: ({ signal }) =>
      api.forecast.projection(options.ledgerId()!, options.scenarioId(), projectionQuery.value, signal),
    enabled: active,
    retry: false,
  })

  const targetEvaluationQuery = computed<ForecastTargetEvaluationQuery>(() => ({
    from: formatLocalDate(options.from()),
    through: formatLocalDate(options.through()),
  }))
  const targetEvaluationEnabled = computed(() => active.value && options.targetId() !== undefined)
  const targetEvaluationResult = useQuery({
    queryKey: computed(() =>
      forecastKeys.targetEvaluation(
        options.ledgerId() ?? '',
        options.scenarioId(),
        options.targetId() ?? '',
        targetEvaluationQuery.value,
      ),
    ),
    queryFn: ({ signal }) =>
      api.forecast.targetEvaluation(
        options.ledgerId()!,
        options.scenarioId(),
        options.targetId()!,
        targetEvaluationQuery.value,
        signal,
      ),
    enabled: targetEvaluationEnabled,
    retry: false,
  })

  function invalidate(): void {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return
    for (const key of forecastKeys.invalidatedByDriverWrite(ledgerId, options.scenarioId())) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const createVariableMutation = useMutation({
    mutationFn: (input: AddForecastVariableInput) =>
      api.forecast.createVariable(options.ledgerId()!, options.scenarioId(), input),
    onSuccess: invalidate,
  })

  const deleteVariableMutation = useMutation({
    mutationFn: (variableId: string) =>
      api.forecast.deleteVariable(options.ledgerId()!, options.scenarioId(), variableId),
    onSuccess: invalidate,
  })

  const createEventMutation = useMutation({
    mutationFn: (input: AddForecastEventInput) =>
      api.forecast.createEvent(options.ledgerId()!, options.scenarioId(), input),
    onSuccess: invalidate,
  })

  const deleteEventMutation = useMutation({
    mutationFn: (eventId: string) =>
      api.forecast.deleteEvent(options.ledgerId()!, options.scenarioId(), eventId),
    onSuccess: invalidate,
  })

  return {
    scenario: computed(() => scenarioQuery.data.value),
    variables: computed(() => variablesQuery.data.value ?? []),
    events: computed(() => eventsQuery.data.value ?? []),
    projection: computed(() => projectionResult.data.value),
    isProjecting: computed(() => active.value && projectionResult.isPending.value),
    projectionError: computed(() => projectionResult.error.value),
    projectionRetrying: retrying(projectionResult),
    targetEvaluation: computed(() => targetEvaluationResult.data.value),
    isLoading: computed(
      () =>
        active.value &&
        (scenarioQuery.isPending.value || variablesQuery.isPending.value || eventsQuery.isPending.value),
    ),
    error: computed(() => scenarioQuery.error.value ?? variablesQuery.error.value ?? eventsQuery.error.value),
    notFound: computed(() => isNotFound(scenarioQuery.error.value)),
    retrying: retrying(scenarioQuery, variablesQuery, eventsQuery),
    isCreatingVariable: computed(() => createVariableMutation.isPending.value),
    hasVariableError: computed(() => createVariableMutation.error.value !== null),
    variableErrorDetail: computed(() => problemDetail(createVariableMutation.error.value)),
    deletingVariableId: computed(() =>
      deleteVariableMutation.isPending.value
        ? (deleteVariableMutation.variables.value as string | undefined)
        : undefined,
    ),
    isCreatingEvent: computed(() => createEventMutation.isPending.value),
    hasEventError: computed(() => createEventMutation.error.value !== null),
    eventErrorDetail: computed(() => problemDetail(createEventMutation.error.value)),
    deletingEventId: computed(() =>
      deleteEventMutation.isPending.value
        ? (deleteEventMutation.variables.value as string | undefined)
        : undefined,
    ),
    createVariable: (input) => createVariableMutation.mutateAsync(input),
    deleteVariable: (variableId) => deleteVariableMutation.mutateAsync(variableId),
    createEvent: (input) => createEventMutation.mutateAsync(input),
    deleteEvent: (eventId) => deleteEventMutation.mutateAsync(eventId),
    retry: () => {
      void scenarioQuery.refetch()
      void variablesQuery.refetch()
      void eventsQuery.refetch()
      void projectionResult.refetch()
    },
  }
}
